import { apiFetch, type ApiResponse } from './api';

/**
 * Response from the backend ip-lookup proxy. Backed by ipinfo.io Lite,
 * which returns country + ASN only — no city, region, coordinates, hostname,
 * or timezone. Lite is unlimited free.
 * https://ipinfo.io/developers/lite-api
 */
export interface IpLookupResult {
  ip: string;
  asn: string | null;
  asName: string | null;
  asDomain: string | null;
  countryCode: string | null;
  country: string | null;
  continentCode: string | null;
  continent: string | null;
  lookedUpAt: string;
}

/**
 * Returns true for IPs we don't bother sending to the lookup proxy:
 * RFC1918, loopback, link-local, CGNAT, multicast/reserved, and IPv6 ULA/loopback/link-local.
 * Anything that's already not a public, routable IP.
 *
 * This is the client-side gate that hides the Lookup button — the backend
 * applies the same check as defence-in-depth.
 */
export function isPrivateOrReservedIp(ip: string): boolean {
  if (!ip) return true;
  const trimmed = ip.trim();

  // IPv4
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(trimmed);
  if (v4) {
    const [a, b] = [Number(v4[1]), Number(v4[2])];
    if ([a, Number(v4[2]), Number(v4[3]), Number(v4[4])].some((n) => n > 255)) {
      return true; // malformed octet — refuse
    }
    if (a === 10) return true;
    if (a === 127) return true;
    if (a === 169 && b === 254) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
    if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT
    if (a === 0) return true;
    if (a >= 224) return true; // multicast + reserved + broadcast
    return false;
  }

  // IPv6 — only catch the obvious unroutable cases; let everything else through
  // and rely on the backend bogon check for edges.
  const lower = trimmed.toLowerCase();
  if (!lower.includes(':')) return true; // not v4 and not v6 → refuse
  if (lower === '::1' || lower === '::') return true;
  if (/^fe[89ab]/.test(lower)) return true; // fe80::/10 link-local
  if (/^f[cd]/.test(lower)) return true; // fc00::/7 ULA
  return false;
}

export async function lookupIp(ip: string): Promise<ApiResponse<IpLookupResult>> {
  // apiFetch already prepends API_BASE_URL ('/api'); passing '/api/...' here
  // produced '/api/api/ip-lookup/…' and a 404 in every environment.
  return apiFetch<IpLookupResult>(`/ip-lookup/${encodeURIComponent(ip)}`);
}
